import { registerPlugin, Capacitor } from '@capacitor/core';
import { Injectable } from '@angular/core';

/**
 * Interfaz del plugin nativo custom `AlternateIcon`.
 *
 * Implementaciones nativas:
 *  - iOS  → `ios/App/App/Plugins/AlternateIconPlugin.swift`
 *  - Android → `android/app/src/main/java/com/sidedrawer/app/plugins/AlternateIconPlugin.kt`
 *
 * El plugin acepta ambos nombres en el mismo payload para simplificar
 * el caller (el TS no necesita saber en qué plataforma corre).
 */
export interface AlternateIconPlugin {
  /**
   * iOS: llama `UIApplication.setAlternateIconName(iosName)` inmediato.
   *       `iosName = null` vuelve al ícono primary.
   * Android: guarda `androidAlias` como swap pendiente para ejecutar
   *          al próximo `ON_STOP` del ProcessLifecycleOwner.
   *          `androidAlias = null` equivale a activar el alias default.
   */
  setIcon(options: {
    iosName: string | null;
    androidAlias: string | null;
  }): Promise<void>;

  /** Devuelve el nombre del alternate icon actualmente activo (iOS) o null si es el primary. */
  getCurrentIconName(): Promise<{ name: string | null }>;
}

const NativePlugin = registerPlugin<AlternateIconPlugin>('AlternateIcon');

/**
 * Wrapper inyectable del plugin nativo. En web (no-native) los métodos
 * son no-ops seguros que resuelven inmediatamente.
 */
@Injectable({ providedIn: 'root' })
export class AlternateIconService {
  async setIcon(options: {
    iosName: string | null;
    androidAlias: string | null;
  }): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    try {
      await NativePlugin.setIcon(options);
    } catch (err) {
      console.warn('[AlternateIcon] setIcon failed', err);
    }
  }

  async getCurrentIconName(): Promise<string | null> {
    if (!Capacitor.isNativePlatform()) {
      return null;
    }
    try {
      const { name } = await NativePlugin.getCurrentIconName();
      return name;
    } catch (err) {
      console.warn('[AlternateIcon] getCurrentIconName failed', err);
      return null;
    }
  }
}
